import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ExternalLink,
  Phone,
  Star,
  Waves,
  Coffee,
  Wifi,
  CarFront,
  ConciergeBell,
  PawPrint,
  Presentation,
  Truck,
  ChevronRight,
  ArrowUpRight,
  Sparkles,
  Building2,
  DoorOpen,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Picture, findPhoto } from "../lib/photos";
import { Lightbox } from "../components/Lightbox";
import { RoomCard } from "../components/RoomCard";
import { useSeo } from "../lib/seo";
import { BOOKING_URL, PHONE_TEL, PHONE_DISPLAY } from "../components/Header";
import {
  GoogleReviewShowcase,
  GoogleG,
} from "../components/GoogleReviews";
import {
  GOOGLE_READ_REVIEWS_URL,
  GOOGLE_WRITE_REVIEW_URL,
} from "../lib/reviews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Waves,
  Coffee,
  Wifi,
  CarFront,
  ConciergeBell,
  PawPrint,
  Presentation,
  Truck,
};

const ATTRACTIONS = [
  { name: "Rathbun Lake", slug: "rathbun-lake", distance: "7 miles", note: "Iowa's third-largest lake — boating, fishing, and swimming beaches." },
  { name: "Honey Creek Resort", slug: "honey-creek-resort", distance: "20 min drive", note: "State park resort with golf, marina, and family cabins on Rathbun." },
  { name: "Centerville Historic Square", slug: "centerville-square", distance: "10 min walk", note: "One of the largest town squares in the country — shops, cafés, courthouse." },
  { name: "Appanoose County Coal Mining Museum", slug: "coal-mining-museum", distance: "1 mile", note: "The story of the mines that built this corner of southern Iowa." },
  { name: "Sharon Bluffs State Park", slug: "sharon-bluffs-state-park", distance: "15 min drive", note: "Wooded bluffs above the Chariton River — trails, camping, wildlife." },
  { name: "Appanoose County Courthouse", slug: "centerville-square", distance: "10 min walk", note: "Anchor of the historic square and a beloved local landmark." },
];

function Section({ id, className = "", children, testId }) {
  return (
    <section id={id} className={`relative ${className}`} data-testid={testId}>
      {children}
    </section>
  );
}

export default function Home() {
  const [photos, setPhotos] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [ph, rm, am, fq] = await Promise.all([
          axios.get(`${API}/photos`).then((r) => r.data),
          axios.get(`${API}/rooms`).then((r) => r.data),
          axios.get(`${API}/amenities`).then((r) => r.data),
          axios.get(`${API}/faqs`).then((r) => r.data),
        ]);
        if (!alive) return;
        setPhotos(ph.photos || []);
        setRooms(rm || []);
        setAmenities(am || []);
        setFaqs(fq || []);
      } catch (e) {
        console.error("data fetch fail", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const heroPhoto = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-exterior-hero"),
    [photos],
  );
  const poolPhoto = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-indoor-pool-wide") ||
          findPhoto(photos, "westbridge-inn-suites-centerville-iowa-indoor-swimming-pool"),
    [photos],
  );
  const breakfastPhoto = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-belgian-waffle-breakfast") ||
          findPhoto(photos, "westbridge-inn-suites-centerville-iowa-breakfast-area"),
    [photos],
  );
  const exteriorPhoto = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-exterior-driveway"),
    [photos],
  );

  // Ordered curated gallery — mix categories for editorial rhythm
  const galleryOrder = [
    "exterior-hero",
    "indoor-swimming-pool",
    "belgian-waffle-breakfast",
    "king-suite-spa-bath",
    "double-queen-room",
    "meeting-room-projector",
    "guest-room-01",
    "hot-breakfast",
    "exterior-signage",
    "jacuzzi-suite",
    "conference-room-wide",
    "indoor-pool-wide",
  ];
  const gallery = useMemo(() => {
    const arr = galleryOrder
      .map((slugPart) => photos.find((p) => p.slug.includes(slugPart)))
      .filter(Boolean);
    // dedupe
    const seen = new Set();
    return arr.filter((p) => (seen.has(p.slug) ? false : seen.add(p.slug) || true));
  }, [photos]);

  const roomWithPhoto = (r) => ({ ...r, photo: findPhoto(photos, r.photo_slug) });

  // SEO / JSON-LD -------------------------------------------------------
  const canonical = "https://westbridgehotels.com/";
  const ogImage = heroPhoto?.jpg
    ? `${window.location.origin}${heroPhoto.jpg}`
    : undefined;

  const jsonLd = useMemo(() => {
    const hotelLd = {
      "@context": "https://schema.org",
      "@type": "Hotel",
      "@id": `${canonical}#hotel`,
      name: "Westbridge Inn & Suites",
      alternateName: "Westbridge Inn and Suites Centerville",
      url: canonical,
      telephone: "+1-641-437-7272",
      email: "Centerville@westbridgehotels.com",
      priceRange: "$$",
      description:
        "The largest and newest lodging in southern Iowa. Indoor pool, free hot breakfast, pet-friendly, 24-hour front desk. Just 7 miles from Rathbun Lake in Centerville, IA.",
      address: {
        "@type": "PostalAddress",
        streetAddress: "117 N 18th St",
        addressLocality: "Centerville",
        addressRegion: "IA",
        postalCode: "52544",
        addressCountry: "US",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 40.733901,
        longitude: -92.867487,
      },
      checkinTime: "15:00",
      checkoutTime: "11:00",
      numberOfRooms: 52,
      petsAllowed: true,
      amenityFeature: [
        { "@type": "LocationFeatureSpecification", name: "Indoor pool", value: true },
        { "@type": "LocationFeatureSpecification", name: "Free breakfast", value: true },
        { "@type": "LocationFeatureSpecification", name: "Free WiFi", value: true },
        { "@type": "LocationFeatureSpecification", name: "Free parking", value: true },
        { "@type": "LocationFeatureSpecification", name: "24-hour front desk", value: true },
        { "@type": "LocationFeatureSpecification", name: "Pet friendly", value: true },
        { "@type": "LocationFeatureSpecification", name: "Meeting room", value: true },
        { "@type": "LocationFeatureSpecification", name: "Truck parking", value: true },
        { "@type": "LocationFeatureSpecification", name: "ADA accessible", value: true },
      ],
      image: heroPhoto ? [`${window.location.origin}${heroPhoto.jpg}`] : undefined,
      logo: `${window.location.origin}/brand/logo.png`,
    };
    const orgLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Westbridge Inn & Suites",
      url: canonical,
      logo: `${window.location.origin}/brand/logo.png`,
      sameAs: [],
    };
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (faqs || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const imageLds = gallery.map((p) => ({
      "@context": "https://schema.org",
      "@type": "ImageObject",
      contentUrl: `${window.location.origin}${p.jpg}`,
      caption: p.caption || p.alt,
      description: p.alt,
    }));
    return [hotelLd, orgLd, faqLd, ...imageLds];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqs, gallery, heroPhoto]);

  useSeo({
    title:
      "Westbridge Inn & Suites — Best Hotel in Centerville, Iowa (Book Direct)",
    description:
      "The largest and newest lodging in southern Iowa. Indoor pool, free hot breakfast, pet-friendly, 24-hour front desk. Just 7 miles from Rathbun Lake — book direct and save.",
    canonical,
    image: ogImage,
    jsonLd,
  });

  return (
    <main id="main" data-testid="home-main">
      {/* ============================ HERO ============================ */}
      <Section
        id="top"
        testId="hero-section"
        className="min-h-[92svh] md:min-h-[100svh] flex flex-col justify-end overflow-hidden"
      >
        <div className="absolute inset-0">
          {heroPhoto ? (
            <Picture
              photo={heroPhoto}
              className="absolute inset-0 w-full h-full"
              imgClassName="w-full h-full object-cover"
              sizes="100vw"
              fetchPriority="high"
              loading="eager"
              testId="hero-image"
            />
          ) : (
            <div className="absolute inset-0 bg-forest-800" />
          )}
          {/* scrim */}
          <div className="absolute inset-0 bg-gradient-to-t from-forest-900/95 via-forest-900/45 to-forest-900/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest-900/30 via-transparent to-transparent" />
        </div>

        <div className="container-app relative pb-14 md:pb-24 pt-28 md:pt-32 text-cream animate-fade-up max-w-5xl">
          <p className="eyebrow mb-5 text-brass-300">
            Centerville, Iowa · Since 2005 · Formerly Motel 60
          </p>
          <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl lg:text-[5.6rem] tracking-tight leading-[0.98] max-w-4xl">
            Westbridge Inn &amp; Suites — genuine hospitality, minutes from the historic square.
          </h1>
          <p className="mt-7 text-lg md:text-xl font-light text-cream/85 max-w-2xl leading-relaxed">
            52 rooms. An indoor pool with an ADA lift. Belgian waffles at 6 a.m. And a real
            person at the front desk — day or night.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-base"
              data-testid="hero-book-now"
            >
              Book Direct &amp; Save <ArrowUpRight size={16} />
            </a>
            <a href={`tel:${PHONE_TEL}`} className="btn-outline text-base" data-testid="hero-call-now">
              <Phone size={16} /> Call {PHONE_DISPLAY}
            </a>
          </div>
          <div className="mt-14 grid grid-cols-3 gap-6 md:gap-14 max-w-xl text-cream/80">
            <Stat label="Rooms" value="52" />
            <Stat label="From Rathbun Lake" value="7 mi" />
            <Stat label="Front Desk" value="24/7" />
          </div>
        </div>
      </Section>

      {/* ============================ VALUE PROP ============================ */}
      <Section id="value" className="bg-cream py-24 md:py-36">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-20 items-end">
          <div className="md:col-span-7">
            <p className="eyebrow mb-5">Book Direct &amp; Save</p>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] text-forest-900 tracking-tight">
              Skip the middlemen. Book direct on our booking engine —{" "}
              <span className="text-brass italic">no third-party fees</span>, no drama.
            </h2>
          </div>
          <div className="md:col-span-5 space-y-6 text-ink/85 text-base md:text-lg leading-relaxed">
            <p>
              You'll pay the same rate the front desk sees. If the pool is closed for winter
              maintenance, we'll tell you before you pay. If a room is exterior-access, we say
              so on this page. We'd rather earn your second night than upsell the first.
            </p>
            <p className="font-medium text-forest-900">
              Trucker rate available — call the front desk. Kennels on request. Meeting room
              rentable by the hour.
            </p>
          </div>
        </div>
      </Section>

      {/* ============================ GALLERY ============================ */}
      <Section id="gallery" className="bg-cream-100 py-20 md:py-28 relative paper-grain" testId="gallery-section">
        <div className="container-app relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
            <div className="max-w-xl">
              <p className="eyebrow mb-4">Real photos. Real property.</p>
              <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-tight">
                Every photo on this page was taken here.
              </h2>
            </div>
            <p className="text-ink/70 max-w-md leading-relaxed">
              We won't decorate the internet with someone else's hotel. Tap any image to open
              the lightbox.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-3 md:gap-4">
            {gallery.slice(0, 10).map((p, i) => {
              // asymmetric bento — carefully chosen spans
              const spans = [
                "col-span-12 md:col-span-8 aspect-[16/10]",
                "col-span-6 md:col-span-4 aspect-[4/5]",
                "col-span-6 md:col-span-4 aspect-square",
                "col-span-12 md:col-span-4 aspect-[4/3]",
                "col-span-6 md:col-span-4 aspect-[4/5]",
                "col-span-6 md:col-span-3 aspect-square",
                "col-span-12 md:col-span-5 aspect-[16/10]",
                "col-span-12 md:col-span-4 aspect-[4/3]",
                "col-span-6 md:col-span-4 aspect-square",
                "col-span-6 md:col-span-4 aspect-[4/5]",
              ];
              return (
                <button
                  key={p.slug}
                  onClick={() => setLightboxIdx(i)}
                  className={`group relative overflow-hidden bg-forest-800 ${spans[i] || "col-span-6 aspect-square"}`}
                  data-testid={`gallery-tile-${i}`}
                  aria-label={`Open lightbox: ${p.alt}`}
                >
                  <Picture
                    photo={p}
                    className="absolute inset-0 w-full h-full"
                    imgClassName="w-full h-full object-cover"
                    sizes="(min-width: 768px) 40vw, 100vw"
                  />
                  <div className="absolute inset-0 bg-forest-900/0 group-hover:bg-forest-900/20 transition-colors duration-500" />
                </button>
              );
            })}
          </div>
        </div>
        <Lightbox
          photos={gallery.slice(0, 10)}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(-1)}
          onPrev={() =>
            setLightboxIdx((i) => (i - 1 + Math.min(10, gallery.length)) % Math.min(10, gallery.length))
          }
          onNext={() => setLightboxIdx((i) => (i + 1) % Math.min(10, gallery.length))}
        />
      </Section>

      {/* ============================ INDOOR POOL ============================ */}
      <Section id="pool" className="bg-forest-900 text-cream py-24 md:py-32" testId="pool-section">
        <div className="container-app grid md:grid-cols-2 gap-10 md:gap-20 items-center">
          <div className="relative order-2 md:order-1">
            <div className="block w-full aspect-[4/5] md:aspect-[4/5] overflow-hidden bg-forest-800">
              {poolPhoto && (
                <Picture
                  photo={poolPhoto}
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover"
                  sizes="(min-width: 768px) 44vw, 100vw"
                  testId="pool-image"
                />
              )}
            </div>
            <span className="absolute -bottom-4 left-4 md:-left-8 bg-brass text-forest-900 font-medium text-sm px-4 py-2 tracking-wide">
              Ground floor · ADA lift
            </span>
          </div>
          <div className="order-1 md:order-2 max-w-lg">
            <p className="eyebrow text-brass-300 mb-4">Indoor Pool</p>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-cream">
              A rainy-day <span className="italic text-brass-300">swimming holiday</span>.
            </h2>
            <p className="mt-6 text-cream/85 text-lg leading-relaxed">
              Our indoor pool sits on the ground floor with an accessible ADA lift, so guests
              of every ability can enjoy the water. Whether the sun is out or the rain is
              drumming on the windows, the pool is warm and open to every guest.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center text-sm text-cream/80">
              <span className="inline-flex items-center gap-2">
                <Sparkles size={16} className="text-brass-300" /> Heated year-round
              </span>
              <span className="hidden sm:inline text-brass-300">·</span>
              <span>Seasonal maintenance: Nov – mid Jan</span>
            </div>
          </div>
        </div>
      </Section>

      {/* ============================ INTERIOR vs EXTERIOR ============================ */}
      <Section id="corridor" className="bg-cream py-24 md:py-32">
        <div className="container-app">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">Two ways to stay</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-tight">
              Interior corridor or park-at-your-door — you choose.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 md:gap-10">
            <div className="bg-cream-50 border border-tan p-8 md:p-12 relative overflow-hidden" data-testid="corridor-interior">
              <Building2 size={28} className="text-brass mb-6" />
              <h3 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">
                Interior Corridor <span className="text-brass">·</span> 22 rooms
              </h3>
              <p className="text-ink/80 leading-relaxed">
                Enter through the lobby, take the stairs or the elevator, then walk to your
                door in a climate-controlled hallway. Quieter, warmer in winter, and next to
                the pool and breakfast room.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-ink/75">
                <li>· Quieter for light sleepers</li>
                <li>· Steps from the indoor pool</li>
                <li>· Best for winter stays</li>
              </ul>
            </div>
            <div className="bg-forest-900 text-cream p-8 md:p-12 relative overflow-hidden" data-testid="corridor-exterior">
              <DoorOpen size={28} className="text-brass-300 mb-6" />
              <h3 className="font-display text-2xl md:text-3xl mb-3">
                Exterior Access <span className="text-brass-300">·</span> 30 rooms
              </h3>
              <p className="text-cream/85 leading-relaxed">
                Park at your door on our east wing. A favorite with truckers, construction
                crews, and anyone with an early tee-time at Honey Creek. No hallways, no
                lobbies — key in the door and go.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-cream/75">
                <li>· Park directly outside your room</li>
                <li>· Truck-friendly, easy in / easy out</li>
                <li>· Late-arrival or early-departure friendly</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* ============================ ROOMS ============================ */}
      <Section id="rooms" className="bg-cream-100 py-24 md:py-32 paper-grain" testId="rooms-section">
        <div className="container-app relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-xl">
              <p className="eyebrow mb-4">Rooms &amp; Suites</p>
              <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-tight">
                Five honest layouts. One quiet promise.
              </h2>
            </div>
            <a
              href="/rooms"
              className="btn-ghost-dark text-sm self-start md:self-end"
              data-testid="rooms-book-link"
            >
              See all rooms <ArrowUpRight size={14} />
            </a>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {rooms.map((r0) => {
              const r = roomWithPhoto(r0);
              return <RoomCard key={r.slug} room={r} photo={r.photo} />;
            })}
          </div>
        </div>
      </Section>

      {/* ============================ AMENITIES ============================ */}
      <Section id="amenities" className="bg-cream py-24 md:py-32" testId="amenities-section">
        <div className="container-app grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="eyebrow mb-4">Amenities</p>
            <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] text-forest-900 tracking-tight">
              Everything included. Nothing hidden.
            </h2>
            <p className="mt-6 text-ink/75 leading-relaxed">
              The stuff you actually use, all included with every night. No parking fees, no
              breakfast upsells, no resort fees pretending to be resort fees.
            </p>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-12">
            {amenities.map((a) => {
              const Icon = ICON_MAP[a.icon] || Sparkles;
              return (
                <div
                  key={a.key}
                  className="flex gap-4"
                  data-testid={`amenity-${a.key}`}
                >
                  <Icon size={22} className="text-brass mt-1 flex-none" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-display text-xl text-forest-900 mb-1.5">{a.label}</h3>
                    <p className="text-sm text-ink/75 leading-relaxed">{a.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ============================ CELEBRATE & MEET ============================ */}
      <Section id="celebrate-meet" className="bg-cream-100 py-24 md:py-32 paper-grain" testId="celebrate-meet-section">
        <div className="container-app">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow mb-4">Celebrate &amp; meet</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
              A quiet suite for the couple. A room with a projector for the team.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <a
              href="/rooms/king-jacuzzi-suite"
              className="group relative bg-forest-900 text-cream overflow-hidden hover:-translate-y-0.5 transition-transform"
              data-testid="celebrate-couples-teaser"
            >
              <div className="aspect-[4/3] overflow-hidden">
                {(findPhoto(photos, "westbridge-inn-suites-centerville-iowa-jacuzzi-suite")) && (
                  <Picture
                    photo={findPhoto(photos, "westbridge-inn-suites-centerville-iowa-jacuzzi-suite")}
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700"
                    sizes="(min-width: 768px) 45vw, 100vw"
                  />
                )}
              </div>
              <div className="p-8 md:p-10">
                <p className="eyebrow text-brass-300 mb-3">King Jacuzzi Suite</p>
                <h3 className="font-display text-2xl md:text-3xl leading-tight">
                  A King Jacuzzi Suite for the anniversary weekend.
                </h3>
                <p className="mt-4 text-cream/80 leading-relaxed">
                  Our largest room. A king bed, a private in-room jacuzzi tub, and a quiet interior-corridor location — two blocks off the historic square. Anniversaries, honeymoons, birthdays, proposals, babymoons.
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-brass-300 group-hover:gap-3 transition-all">
                  See the suite <ArrowUpRight size={16} />
                </span>
                <span className="mt-3 block">
                  <a
                    href="/stay/couples-special-occasions"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-cream/70 hover:text-brass-300 underline underline-offset-4"
                    data-testid="celebrate-couples-secondary-link"
                  >
                    Or read our couples &amp; special-occasions guide →
                  </a>
                </span>
              </div>
            </a>
            <a
              href="/meetings-events"
              className="group relative bg-cream-50 border border-tan overflow-hidden hover:-translate-y-0.5 transition-transform"
              data-testid="celebrate-meetings-teaser"
            >
              <div className="aspect-[4/3] overflow-hidden">
                {(findPhoto(photos, "westbridge-inn-suites-centerville-iowa-conference-room")) && (
                  <Picture
                    photo={findPhoto(photos, "westbridge-inn-suites-centerville-iowa-conference-room")}
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                    sizes="(min-width: 768px) 45vw, 100vw"
                  />
                )}
              </div>
              <div className="p-8 md:p-10">
                <p className="eyebrow text-brass mb-3">Meetings &amp; events</p>
                <h3 className="font-display text-2xl md:text-3xl text-forest-900 leading-tight">
                  A meeting room for the team — and space for a shower or celebration.
                </h3>
                <p className="mt-4 text-ink/75 leading-relaxed">
                  Projector-equipped meeting room, chair covers and simple table setups, free Wi-Fi, free parking. Small business meetings, trainings, showers, birthdays, small receptions. Call the front desk to plan.
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-forest-900 group-hover:text-brass group-hover:gap-3 transition-all">
                  Explore meetings &amp; events <ArrowUpRight size={16} />
                </span>
              </div>
            </a>
          </div>
        </div>
      </Section>

      {/* ============================ REVIEWS ============================ */}
      <Section id="reviews" className="bg-forest-800 text-cream py-24 md:py-32" testId="reviews-section">
        <div className="container-app">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 max-w-5xl">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <GoogleG size={22} />
                <p className="eyebrow text-brass-300 mb-0">Reviews on Google</p>
              </div>
              <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] text-cream tracking-tight">
                Real words from the guests who&rsquo;ve stayed here.
              </h2>
              <p className="mt-5 text-cream/70 max-w-lg leading-relaxed">
                Every review below is a real 5-star quote from a real guest. No paraphrasing, no invention. Read the full stream on Google, or add yours if you&rsquo;ve stayed with us.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={GOOGLE_READ_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost-light text-xs"
                data-testid="reviews-read-all-google"
              >
                Read all on Google <ExternalLink size={12} />
              </a>
              <a
                href={GOOGLE_WRITE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-xs"
                data-testid="reviews-write-google"
              >
                Write a review <Star size={12} fill="currentColor" strokeWidth={0} />
              </a>
            </div>
          </div>
          <GoogleReviewShowcase />
        </div>
      </Section>

      {/* ============================ ATTRACTIONS ============================ */}
      <Section id="explore" className="bg-cream py-24 md:py-32" testId="explore-section">
        <div className="container-app">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="eyebrow mb-4">Explore local</p>
              <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] text-forest-900 tracking-tight">
                Six good reasons to stay a second night.
              </h2>
            </div>
            <p className="text-ink/70 max-w-sm">
              Southern Iowa is quieter than the guidebooks suggest — that's the point.
            </p>
          </div>
        </div>
        <div className="container-app md:hidden overflow-x-auto no-scrollbar -mx-5 px-5">
          <div className="flex gap-4 min-w-full snap-x snap-mandatory">
            {ATTRACTIONS.map((a) => (
              <AttractionCard key={a.name} a={a} className="snap-start shrink-0 w-[78%]" />
            ))}
          </div>
        </div>
        <div className="container-app hidden md:grid grid-cols-3 gap-5">
          {ATTRACTIONS.map((a) => (
            <AttractionCard key={a.name} a={a} />
          ))}
        </div>
      </Section>

      {/* ============================ WHY WESTBRIDGE ============================ */}
      <Section id="why" className="bg-cream-100 py-24 md:py-32 paper-grain">
        <div className="container-app relative grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="block w-full aspect-[4/3] overflow-hidden bg-forest-800">
              {breakfastPhoto && (
                <Picture
                  photo={breakfastPhoto}
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover"
                  sizes="(min-width: 768px) 45vw, 100vw"
                />
              )}
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="eyebrow mb-4">Why Westbridge</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
              A newer property. A quieter street. An owner who answers the phone.
            </h2>
            <ul className="mt-8 space-y-4 text-ink/85">
              {[
                "The largest and newest lodging in southern Iowa — 52 well-kept rooms.",
                "Free Belgian waffles, cereal, fresh fruit, bagels — every morning from 6 AM.",
                "Fast, free WiFi in every room and every public space.",
                "24-hour front desk. A real person, not a phone tree.",
                "Pet friendly. Trucker friendly. Family friendly. Really.",
              ].map((line) => (
                <li key={line} className="flex gap-3">
                  <span className="mt-2.5 w-2 h-2 rounded-full bg-brass shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ============================ FAQ ============================ */}
      <Section id="faq" className="bg-cream py-24 md:py-32" testId="faq-section">
        <div className="container-app">
          {/* Full-width heading band — no more sticky-heading + long-column split. */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
            <div className="max-w-2xl">
              <p className="eyebrow mb-4">Common questions</p>
              <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] text-forest-900 tracking-tight">
                Short answers to the questions we hear most.
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <a
                href="/faq"
                className="text-sm text-forest-900 brass-underline inline-flex items-center gap-2"
                data-testid="faq-see-all"
              >
                See all 100+ questions <ArrowUpRight size={14} />
              </a>
              <a
                href="mailto:Centerville@westbridgehotels.com"
                className="text-sm text-forest-900 brass-underline inline-flex items-center gap-2"
                data-testid="faq-mailto"
              >
                Something else? Email us <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          {/* Two independent columns on desktop so expanding one doesn't reflow the other.
              Curated to 8 items instead of 102 — the full corpus lives at /faq. */}
          {(() => {
            const HOME_KEYS = [
              "check-in", "pool", "pets", "breakfast",
              "wi-fi", "parking", "hot tub", "book a room",
            ];
            const curated = [];
            for (const key of HOME_KEYS) {
              const hit = faqs.find(
                (f) => f.q.toLowerCase().includes(key) && !curated.includes(f),
              );
              if (hit) curated.push(hit);
            }
            const list = curated.slice(0, 8);
            const left = list.filter((_, i) => i % 2 === 0);
            const right = list.filter((_, i) => i % 2 === 1);
            const renderCol = (arr, colId) => (
              <Accordion type="single" collapsible className="divide-y divide-tan/60 border-y border-tan/60">
                {arr.map((f, i) => (
                  <AccordionItem
                    key={`${colId}-${i}`}
                    value={`${colId}-item-${i}`}
                    className="border-none"
                  >
                    <AccordionTrigger
                      className="text-left font-display text-base md:text-lg text-forest-900 py-4 md:py-4 hover:no-underline"
                      data-testid={`faq-trigger-${colId}-${i}`}
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-ink/80 text-sm md:text-base leading-relaxed pb-4 pr-6">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            );
            return (
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-0 items-start">
                <div>{renderCol(left, "l")}</div>
                <div>{renderCol(right, "r")}</div>
              </div>
            );
          })()}
        </div>
      </Section>

      {/* ============================ MAP & OFFERS ============================ */}
      <Section id="find-us" className="bg-forest-900 text-cream py-24 md:py-32" testId="map-section">
        <div className="container-app grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5 flex flex-col justify-between">
            <div>
              <p className="eyebrow text-brass-300 mb-4">Find us</p>
              <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] tracking-tight">
                117 N 18th Street, Centerville, Iowa.
              </h2>
              <p className="mt-6 text-cream/80 text-lg leading-relaxed">
                Two blocks off the historic square. Seven miles from Rathbun Lake. Twenty
                minutes to Honey Creek. Ottumwa, Albia and Moulton are all easy day trips.
              </p>
            </div>
            <div className="mt-10 space-y-4">
              <a
                href="https://maps.google.com/?q=117+N+18th+St,+Centerville,+IA+52544"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="map-directions"
              >
                Get directions <ExternalLink size={14} />
              </a>
              <div className="text-sm text-cream/75">
                <p><strong className="text-cream">Truckers:</strong> call the front desk at{" "}
                  <a className="brass-underline text-brass-300" href={`tel:${PHONE_TEL}`}>
                    {PHONE_DISPLAY}
                  </a>{" "}
                  for driver rates and easy-in truck parking.</p>
              </div>
            </div>
          </div>
          <div className="md:col-span-7 aspect-[4/3] md:aspect-[4/3] overflow-hidden border border-forest-700/60">
            <iframe
              title="Map to Westbridge Inn & Suites"
              className="w-full h-full block"
              style={{ filter: "grayscale(0.35) contrast(1.05)" }}
              src="https://www.google.com/maps?q=117%20N%2018th%20St%2C%20Centerville%2C%20IA%2052544&t=m&z=14&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-iframe"
            />
          </div>
        </div>
      </Section>
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="font-display text-3xl md:text-4xl text-cream">{value}</p>
      <p className="text-xs uppercase tracking-[0.18em] text-cream/60 mt-1">{label}</p>
    </div>
  );
}

function AttractionCard({ a, className = "" }) {
  return (
    <a
      href={`/explore/${a.slug}`}
      className={`bg-cream-50 border border-tan p-6 md:p-8 flex flex-col hover:border-brass/60 transition-colors ${className}`}
      data-testid={`attraction-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <p className="eyebrow text-brass mb-2">{a.distance}</p>
      <h3 className="font-display text-xl md:text-2xl text-forest-900 leading-tight mb-3">
        {a.name}
      </h3>
      <p className="text-sm text-ink/75 leading-relaxed">{a.note}</p>
    </a>
  );
}
